// THE CALLS IN AN AI ANSWER — the `meet-rooms` and `meet-transcript` views
// the assistant mounts in its thread and a Fly page places as a section
// (workspace functions/assistantAppViews.js, the `views[]` contract).
//
// meetAiViews() is the PROVIDER, read from the meet EAGER slice; the views'
// components ride the lazy meet context (`fold: 'meet'` — the shell folds it
// through ensureMeetContext before a card mounts). Two views, both in the
// shell's list frame (workspace components/AssistantViewList.js):
//
//   meet-rooms       the workspace's calls: live now (a member in it — the
//                    same presence the header's ongoing-call badge reads),
//                    upcoming (a room scheduled for a calendar event still
//                    ahead) and recent; a row opens the room on /meet
//   meet-transcript  one call's transcript and its summary — the named call,
//                    else the most recent call that has a transcript
//
// OPENING A CALL IS NEVER A JOIN (MEET-AUTOJOIN-1): a row opens the room's
// detail pane or its transcript (`/meet#room=<id>`, the page's own guarded
// deep link); only that pane's Join starts media. The reads are the meet
// page's own (workspaceProject.meet.rooms, .transcriptView), into each view's
// OWN state — never root.rooms or root.utterances. Nothing here writes data.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function meetAiViews() {
  return {
    app: {
      id: 'system/meet',
      navbar: [{ label: 'shell.app.meet', route: '/meet', icon: 'video' }],
      apps: [{ icon: 'video' }]
    },
    fold: 'meet',
    views: [
      {
        id: 'meet-rooms',
        title: 'meet.answer.rooms.title',
        describe:
          "The workspace's video calls: live now, upcoming (scheduled for a calendar event) and recent; show narrows it; search = words in the call name.",
        component: 'MeetRoomsAnswerView',
        icon: 'video',
        hue: 'green',
        size: 'l',
        params: {
          show: {
            type: 'enum',
            values: ['all', 'live', 'upcoming', 'recent'],
            labels: {
              all: 'meet.answer.show.all',
              live: 'meet.roomList.live',
              upcoming: 'meet.answer.show.upcoming',
              recent: 'meet.answer.show.recent'
            },
            default: 'all'
          },
          search: { type: 'string', describe: 'words in the call name' },
          limit: { type: 'number', min: 1, max: 30, default: 10 }
        },
        link: { route: '/meet', query: {} },
        access: { route: '/meet' }
      },
      {
        id: 'meet-transcript',
        title: 'meet.transcript.heading',
        describe:
          "One call's transcript and its summary; room = words in the call name, empty = the most recent call that has a transcript (the last call).",
        component: 'MeetTranscriptAnswerView',
        icon: 'video',
        hue: 'green',
        size: 'l',
        params: {
          room: {
            type: 'string',
            describe: 'words in the call name; empty = the last call with a transcript'
          },
          limit: { type: 'number', min: 1, max: 200, default: 40 }
        },
        link: { route: '/meet', query: {} },
        access: { route: '/meet' }
      }
    ]
  }
}

// The workspace's rooms (the meet page's own list read), mapped by the
// package's row mapper. `{ data: { rooms }, failed }`.
export async function meetRoomsAnswerLoad(el) {
  const host = el || this
  const sdk = (host && host.sdk) || host.call('getSDK')
  if (!sdk || typeof sdk.execute !== 'function') return { data: { rooms: [] }, failed: true }
  try {
    const res = await sdk.execute('workspaceProject.meet.rooms', 'list')
    const rows = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
    return {
      data: { rooms: rows.filter((r) => r && r.id).map((r) => host.call('rowToRoom', r)) }
    }
  } catch (_) {
    return { data: { rooms: [] }, failed: true }
  }
}

// The model: Live now, Upcoming, Recent — `show` narrows to one; `search`
// matches the call name. Live follows the presence the root keeps
// (root.liveParticipants, the call this tab is in) as it changes.
export function meetRoomsAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  if (failed) return { groups: [], note: 'meet.answer.failed' }
  const show = ['all', 'live', 'upcoming', 'recent'].includes(p.show) ? p.show : 'all'
  const limit = Math.max(1, Math.min(30, Math.floor(Number(p.limit)) || 10))
  const words = String(p.search || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  const nameOf = (r) => say(r.name || 'meet.roomList.untitled')
  const rooms = (Array.isArray(d.rooms) ? d.rooms : []).filter(
    (r) => r && r.id && words.every((w) => nameOf(r).toLowerCase().includes(w))
  )
  const live =
    root.liveParticipants && typeof root.liveParticipants === 'object' ? root.liveParticipants : {}
  const inCall = (r) => (Array.isArray(live[r.id]) ? live[r.id] : []).filter((x) => x && !x.leftAt)
  const mine = (r) => !!root.livekitConnected && String(root.activeRoomId || '') === String(r.id)
  const isLive = (r) => !r.endedAt && (inCall(r).length > 0 || mine(r))
  // A room scheduled for a calendar event: the event's start from the shell's
  // calendar window (root.intranetEvents, every workspace) or the calendar's
  // own (root.events).
  const events = []
    .concat(Array.isArray(root.intranetEvents) ? root.intranetEvents : [])
    .concat(Array.isArray(root.events) ? root.events : [])
  const startOf = (r) => {
    if (!r.calendarEventId) return 0
    const ev = events.find((e) => e && String(e.id) === String(r.calendarEventId))
    if (!ev) return 0
    const at = Date.parse(ev.startAt || (ev.date ? ev.date + 'T' + (ev.startTime || '00:00') : ''))
    return Number.isFinite(at) ? at : 0
  }
  const now = Date.now()
  const at = (r) => Date.parse(r.endedAt || r.createdAt || '') || 0
  const liveRooms = rooms.filter(isLive)
  const upcoming = rooms
    .filter((r) => !r.endedAt && !isLive(r) && startOf(r) > now)
    .sort((a, b) => startOf(a) - startOf(b))
  const taken = new Set([...liveRooms, ...upcoming].map((r) => r.id))
  const recent = rooms.filter((r) => !taken.has(r.id)).sort((a, b) => at(b) - at(a))
  const day = (ms, withTime) =>
    String(
      el.call(
        'localizedDate',
        new Date(ms),
        withTime
          ? { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
          : { month: 'short', day: 'numeric' }
      ) || ''
    )
  const open = (r) => ({ call: 'meetAnswerOpen', arg: { id: String(r.id), view: 'room' } })
  const key = (r) => 'm-' + String(r.id).replace(/[^a-zA-Z0-9_-]/g, '')
  const liveRow = (r) => {
    const n = inCall(r).length
    return el.call('assistantViewRow', {
      key: key(r),
      title: nameOf(r),
      meta:
        n > 1
          ? say('meet.roomList.people').replace('{count}', String(n))
          : n === 1
            ? say('meet.roomList.onePerson')
            : say('meet.roomList.live'),
      mark: true,
      open: open(r)
    })
  }
  const upcomingRow = (r) =>
    el.call('assistantViewRow', {
      key: key(r),
      title: nameOf(r),
      meta: day(startOf(r), true),
      icon: 'date',
      open: open(r)
    })
  const recentRow = (r) =>
    el.call('assistantViewRow', {
      key: key(r),
      title: nameOf(r),
      meta: at(r) ? day(at(r), false) : '',
      icon: 'video',
      open: open(r)
    })
  const groups = []
  let budget = limit
  let shown = 0
  let total = 0
  const put = (gkey, labelKey, list, toRow) => {
    total += list.length
    const rows = list.slice(0, Math.max(0, budget)).map(toRow)
    budget -= rows.length
    shown += rows.length
    if (rows.length)
      groups.push(el.call('assistantViewGroup', { key: gkey, label: say(labelKey), rows }))
  }
  if (show === 'all' || show === 'live') put('live', 'meet.answer.group.live', liveRooms, liveRow)
  if (show === 'all' || show === 'upcoming')
    put('upcoming', 'meet.answer.group.upcoming', upcoming, upcomingRow)
  if (show === 'all' || show === 'recent')
    put('recent', 'meet.answer.group.recent', recent, recentRow)
  return { groups, note: groups.length ? '' : 'meet.answer.empty', more: { shown, total } }
}

// One call's transcript: the named call (words in its name, or its id), else
// the most recent call whose transcript has lines. Up to four candidates are
// read at once (the transcriber writes only where the room transcribes, so
// those come first); the newest last line wins. `{ data: { room, utterances,
// summary, named }, failed }`.
export async function meetTranscriptAnswerLoad(el, params) {
  const host = el || this
  const p = params && typeof params === 'object' ? params : {}
  const sdk = (host && host.sdk) || host.call('getSDK')
  const none = (named, failed) => ({
    data: { room: null, utterances: [], summary: '', named },
    failed
  })
  if (!sdk || typeof sdk.execute !== 'function') return none(false, true)
  let rooms = []
  try {
    const res = await sdk.execute('workspaceProject.meet.rooms', 'list')
    const rows = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
    rooms = rows.filter((r) => r && r.id).map((r) => host.call('rowToRoom', r))
  } catch (_) {
    return none(false, true)
  }
  const ask = String(p.room || '').trim()
  const words = ask.toLowerCase().split(/\s+/).filter(Boolean)
  const nameOf = (r) =>
    String(host.call('polyglot', r.name || 'meet.roomList.untitled')).toLowerCase()
  const at = (r) => Date.parse(r.endedAt || r.createdAt || '') || 0
  let candidates = rooms
  if (words.length) {
    const byId = rooms.filter((r) => String(r.id) === ask)
    candidates = byId.length ? byId : rooms.filter((r) => words.every((w) => nameOf(r).includes(w)))
    if (!candidates.length) return none(true, false)
  }
  const ordered = candidates
    .slice()
    .sort((a, b) => Number(!!b.transcribeEnabled) - Number(!!a.transcribeEnabled) || at(b) - at(a))
    .slice(0, 4)
  const reads = await Promise.all(
    ordered.map((room) =>
      Promise.resolve()
        .then(() => sdk.execute('workspaceProject.meet.transcriptView', 'rpc', { roomId: room.id }))
        .then((view) => {
          const raw = view && Array.isArray(view.utterances) ? view.utterances : []
          const utterances = host.call(
            'dedupeUtterances',
            raw.map((r) => host.call('rowToUtterance', r))
          )
          const analysis = view && view.analysis
          const summary = analysis && typeof analysis.summary === 'string' ? analysis.summary : ''
          const last = utterances.length
            ? Date.parse(utterances[utterances.length - 1].startedAt || '') || at(room)
            : 0
          return { room, utterances: Array.isArray(utterances) ? utterances : [], summary, last }
        })
        .catch(() => null)
    )
  )
  const ok = reads.filter(Boolean)
  if (!ok.length) return none(words.length > 0, true)
  const withLines = ok.filter((r) => r.utterances.length || r.summary)
  // A named call answers even with no line yet (its own sentence says so).
  const pick = (withLines.length ? withLines : words.length ? ok : []).sort(
    (a, b) => b.last - a.last || at(b.room) - at(a.room)
  )[0]
  if (!pick) return none(false, false)
  return {
    data: {
      room: pick.room,
      utterances: pick.utterances,
      summary: pick.summary,
      named: words.length > 0
    }
  }
}

// The model: the summary as the lead, then the last call's lines — the
// speaker's initials, the words, "name · time"; the call's name and day label
// the lines. A line opens the transcript on /meet.
export function meetTranscriptAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  const room = d.room
  if (!room) {
    return {
      groups: [],
      note: failed
        ? 'meet.answer.transcript.failed'
        : d.named
          ? 'meet.answer.empty'
          : 'meet.answer.transcript.none'
    }
  }
  const limit = Math.max(1, Math.min(200, Math.floor(Number(p.limit)) || 40))
  const all = (Array.isArray(d.utterances) ? d.utterances : []).filter(
    (u) => u && String(u.text || '').trim()
  )
  // A standing room keeps every call it ever held: the LAST call is the run
  // of lines after the last silence longer than two hours.
  let start = 0
  for (let i = 1; i < all.length; i++) {
    const prev = Date.parse(all[i - 1].startedAt || '') || 0
    const cur = Date.parse(all[i].startedAt || '') || 0
    if (prev && cur && cur - prev > 2 * 60 * 60 * 1000) start = i
  }
  const lines = all.slice(start)
  const time = (iso) => {
    const ms = Date.parse(iso || '')
    return Number.isFinite(ms)
      ? String(el.call('localizedDate', new Date(ms), { hour: '2-digit', minute: '2-digit' }) || '')
      : ''
  }
  const open = { call: 'meetAnswerOpen', arg: { id: String(room.id), view: 'transcript' } }
  const rows = lines.slice(0, limit).map((u, i) => {
    const who = say(el.call('speakerDisplayName', root, u.speakerEmail, u.userId))
    return el.call('assistantViewRow', {
      key: 'u-' + String(u.id || i).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: String(u.text).trim(),
      meta: [who, time(u.startedAt)].filter(Boolean).join(' · '),
      initials: el.call('initials', who) || null,
      open
    })
  })
  const dayMs = Date.parse((lines[0] && lines[0].startedAt) || room.endedAt || room.createdAt || '')
  const dayLabel = Number.isFinite(dayMs)
    ? String(el.call('localizedDate', new Date(dayMs), { month: 'short', day: 'numeric' }) || '')
    : ''
  const label = [say(room.name || 'meet.roomList.untitled'), dayLabel].filter(Boolean).join(' · ')
  const groups = rows.length ? [el.call('assistantViewGroup', { key: 'lines', label, rows })] : []
  return {
    lead: d.summary || '',
    groups,
    note: groups.length || d.summary ? '' : 'meet.transcript.empty',
    more: { shown: rows.length, total: lines.length }
  }
}

// Open a call where it lives: the room's detail pane (`view: 'room'`) or its
// transcript on /meet — the page's own deep link, `#room=<id>[&view=
// transcript]` (pages/index.js _resolveMeetSeed). Never `view=call`: opening
// is not joining (MEET-AUTOJOIN-1). Already on /meet, a pushed hash fires no
// hashchange, so the page's own re-seed is applied here (pages/index.js
// _planHashReseed: the room and its view; a room switch clears the previous
// room's transcript state). `this` is the row.
export function meetAnswerOpen(target) {
  const el = this
  const t = target && typeof target === 'object' ? target : { id: target }
  const id = t.id == null ? '' : String(t.id)
  if (!id || !el || typeof el.router !== 'function') return false
  const transcript = t.view === 'transcript'
  const root = el.state && el.state.root
  const here = String(el.call('stripWorkspacePrefix', String(el.call('readPageRoute') || '')) || '')
  if (root && typeof root.update === 'function' && /^\/meet(\/|$)/.test(here)) {
    const patch = { activeRoomId: id, meetView: transcript ? 'transcript' : 'list' }
    if (String(root.activeRoomId || '') !== id) {
      patch.transcriptLoaded = false
      patch.analysis = undefined
      patch.analyzeError = null
      patch.transcriptPending = false
      patch._meetAnalyzeInFlight = false
    }
    root.update(patch)
  }
  el.router(
    '/meet#room=' + encodeURIComponent(id) + (transcript ? '&view=transcript' : ''),
    el.getRoot()
  )
  return true
}
